import type { Editor } from '@tiptap/react';
import TextStyle from '@tiptap/extension-text-style';

export type InlineMark = 'bold' | 'italic' | 'underline';

export type LiveTextEditor = {
  objectId: string;
  editor: Editor;
  markClean?: () => void;
};

export type MarkTarget = 'inline' | 'box' | 'none';

const lastRange = new WeakMap<Editor, { from: number; to: number }>();

const MARK_PATCH_KEYS = ['fontWeight', 'fontStyle', 'textDecoration'] as const;
const INLINE_TEXT_STYLE_KEYS = ['color', 'fontFamily', 'fontSize', 'fontWeight'] as const;
const BOX_LEVEL_TEXT_KEYS = ['textTransform', 'textAlign', 'verticalAlign', 'lineHeight', 'letterSpacing', 'padding'] as const;

export type InlineStyleQuery = {
  value: string | number | null;
  mixed: boolean;
};

export function isEditorUsable(editor: Editor | null | undefined): editor is Editor {
  if (!editor) return false;
  return !editor.isDestroyed;
}

export function isLiveTextEditor(
  live: LiveTextEditor | null | undefined,
  objectId?: string | null,
): live is LiveTextEditor {
  if (!live || !isEditorUsable(live.editor)) return false;
  if (objectId && live.objectId !== objectId) return false;
  return true;
}

export function shouldWriteBoxMark(editingTextId: string | null, selectedId: string | null): boolean {
  return Boolean(selectedId) && editingTextId !== selectedId;
}

export function resolveMarkTarget(
  editingTextId: string | null,
  selectedId: string | null,
  live: LiveTextEditor | null | undefined,
): MarkTarget {
  if (!selectedId) return 'none';
  if (editingTextId !== selectedId) return 'box';
  return isLiveTextEditor(live, selectedId) ? 'inline' : 'none';
}

export function nextLiveTextEditor(
  current: LiveTextEditor | null,
  event: LiveTextEditor,
  mounted: boolean,
): LiveTextEditor | null {
  if (mounted) return isEditorUsable(event.editor) ? event : current;
  if (current?.objectId === event.objectId && current.editor === event.editor) return null;
  return current;
}

export function isExclusiveMarkPatch(patch: Record<string, unknown>): boolean {
  const keys = Object.keys(patch).filter((key) => patch[key] != null);
  return keys.length > 0 && keys.every((key) => (MARK_PATCH_KEYS as readonly string[]).includes(key));
}

export function isInlineTextStylePatch(patch: Record<string, unknown>): boolean {
  const keys = Object.keys(patch).filter((key) => patch[key] != null);
  return keys.length > 0 && keys.every((key) => (INLINE_TEXT_STYLE_KEYS as readonly string[]).includes(key));
}

export function isBoxLevelTextPatch(patch: Record<string, unknown>): boolean {
  return Object.keys(patch).some((key) => (BOX_LEVEL_TEXT_KEYS as readonly string[]).includes(key));
}

export function markFromPatch(patch: Record<string, unknown>): InlineMark | null {
  if (!isExclusiveMarkPatch(patch)) return null;
  if (patch.fontWeight != null) return 'bold';
  if (patch.fontStyle != null) return 'italic';
  if (patch.textDecoration != null) return 'underline';
  return null;
}

export function rememberTextSelection(editor: Editor): void {
  if (!isEditorUsable(editor)) return;
  const { from, to } = editor.state.selection;
  if (!isUsableRange(editor, from, to)) return;
  lastRange.set(editor, { from, to });
}

export function clearTextSelection(editor: Editor): void {
  lastRange.delete(editor);
}

export function expandPlainWord(text: string, offset: number): { start: number; end: number } {
  const isWord = (ch: string | undefined) => Boolean(ch && /[^\s.,;:!?()[\]{}<>"'“”‘’]/.test(ch));
  let start = Math.max(0, Math.min(text.length, offset));
  let end = start;
  while (start > 0 && isWord(text[start - 1])) start -= 1;
  while (end < text.length && isWord(text[end])) end += 1;
  return { start, end };
}

export const InlineTextStyle = TextStyle.extend({
  addAttributes() {
    return {
      ...(this.parent?.() || {}),
      fontSize: {
        default: null,
        parseHTML: (element) => element.style.fontSize || null,
        renderHTML: (attributes) => (attributes.fontSize ? { style: `font-size: ${attributes.fontSize}` } : {}),
      },
      fontFamily: {
        default: null,
        parseHTML: (element) => element.style.fontFamily || null,
        renderHTML: (attributes) => (attributes.fontFamily ? { style: `font-family: ${attributes.fontFamily}` } : {}),
      },
      fontWeight: {
        default: null,
        parseHTML: (element) => element.style.fontWeight || null,
        renderHTML: (attributes) => (attributes.fontWeight ? { style: `font-weight: ${attributes.fontWeight}` } : {}),
      },
      fontStyle: {
        default: null,
        parseHTML: (element) => element.style.fontStyle || null,
        renderHTML: (attributes) => (attributes.fontStyle ? { style: `font-style: ${attributes.fontStyle}` } : {}),
      },
      textDecoration: {
        default: null,
        parseHTML: (element) => element.style.textDecoration || null,
        renderHTML: (attributes) => (attributes.textDecoration ? { style: `text-decoration: ${attributes.textDecoration}` } : {}),
      },
    };
  },
});

function expandToWord(editor: Editor): { from: number; to: number } {
  try {
    const { $from } = editor.state.selection;
    const start = $from.start();
    const text = $from.parent.textContent;
    const { start: a, end: b } = expandPlainWord(text, $from.parentOffset);
    if (a === b) return { from: $from.pos, to: $from.pos };
    return { from: start + a, to: start + b };
  } catch {
    const { from, to } = editor.state.selection;
    return { from, to };
  }
}

function documentSize(editor: Editor): number {
  return editor.state.doc.content.size;
}

function isUsableRange(editor: Editor, from: number, to: number): boolean {
  const max = documentSize(editor);
  return Number.isFinite(from) && Number.isFinite(to) && from >= 0 && to <= max && from < to;
}

export function resolveInlineRange(editor: Editor, expandWord = false): { from: number; to: number } {
  if (!isEditorUsable(editor)) return { from: 0, to: 0 };
  const current = editor.state.selection;
  if (isUsableRange(editor, current.from, current.to)) {
    const range = { from: current.from, to: current.to };
    lastRange.set(editor, range);
    return range;
  }
  const stored = lastRange.get(editor);
  if (stored && isUsableRange(editor, stored.from, stored.to)) return stored;
  if (expandWord) return expandToWord(editor);
  return { from: current.from, to: current.to };
}

export function rangeHasMark(editor: Editor, mark: InlineMark): boolean {
  if (!isEditorUsable(editor)) return false;
  const type = editor.schema.marks[mark];
  if (!type) return false;
  const range = resolveInlineRange(editor);
  if (!isUsableRange(editor, range.from, range.to)) return editor.isActive(mark);
  try {
    return editor.state.doc.rangeHasMark(range.from, range.to, type);
  } catch {
    return editor.isActive(mark);
  }
}

export function toggleInlineMark(editor: Editor, mark: InlineMark): boolean {
  if (!isEditorUsable(editor)) return false;
  const range = resolveInlineRange(editor);
  if (!isUsableRange(editor, range.from, range.to)) return false;
  lastRange.set(editor, range);
  const chain = editor.chain().focus().setTextSelection(range);
  if (mark === 'bold') chain.toggleBold();
  else if (mark === 'italic') chain.toggleItalic();
  else chain.toggleUnderline();
  return chain.setTextSelection(range).run();
}

export function applyInlineStyle(editor: Editor, patch: Record<string, unknown>): boolean {
  if (!isEditorUsable(editor) || !isInlineTextStylePatch(patch)) return false;
  const range = resolveInlineRange(editor);
  if (!isUsableRange(editor, range.from, range.to)) return false;
  lastRange.set(editor, range);
  const mark: Record<string, string | null> = {};
  if (typeof patch.color === 'string') mark.color = patch.color;
  if (typeof patch.fontFamily === 'string') mark.fontFamily = patch.fontFamily;
  if (patch.fontSize != null) mark.fontSize = `${Number(patch.fontSize) || 24}px`;
  if (patch.fontWeight != null) mark.fontWeight = String(patch.fontWeight);
  if (!Object.keys(mark).length) return false;
  return editor.chain().focus().setTextSelection(range).setMark('textStyle', mark).setTextSelection(range).run();
}

function collectMarkValues(editor: Editor, attr: 'fontFamily' | 'fontSize' | 'color' | 'fontWeight'): Array<string | number> {
  const range = resolveInlineRange(editor);
  if (!isUsableRange(editor, range.from, range.to)) return [];
  const values = new Set<string>();
  editor.state.doc.nodesBetween(range.from, range.to, (node) => {
    for (const mark of node.marks) {
      const value = mark.attrs?.[attr];
      if (value != null && value !== '') values.add(String(value));
    }
  });
  return Array.from(values);
}


export function queryInlineTextStyle(
  editor: Editor,
  attr: 'fontFamily' | 'fontSize' | 'color' | 'fontWeight',
): InlineStyleQuery {
  if (!isEditorUsable(editor)) return { value: null, mixed: false };
  const values = collectMarkValues(editor, attr);
  if (values.length > 1) return { value: null, mixed: true };
  if (values.length === 1) {
    const raw = values[0];
    if (attr === 'fontSize' || attr === 'fontWeight') {
      return { value: Number.parseFloat(String(raw)), mixed: false };
    }
    return { value: String(raw).replace(/['"]/g, ''), mixed: false };
  }
  const active = editor.getAttributes('textStyle')?.[attr];
  if (active == null || active === '') return { value: null, mixed: false };
  if (attr === 'fontSize' || attr === 'fontWeight') return { value: Number.parseFloat(String(active)), mixed: false };
  return { value: String(active).replace(/['"]/g, ''), mixed: false };
}
